<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        Notifications
      </oxd-text>
      <oxd-divider />

      <div
        v-if="message"
        class="orangehrm-notifications-message"
        :class="{
          '--success': messageType === 'success',
          '--error': messageType === 'error',
        }"
      >
        {{ message }}
      </div>

      <form method="post" :action="currentUrl" enctype="multipart/form-data">
        <input v-if="configuration.enabled" type="hidden" name="enabled" value="1" />
        <input type="hidden" name="title" :value="form.title" />
        <input type="hidden" name="body" :value="form.body" />
        <input type="hidden" name="category" :value="form.category" />
        <input type="hidden" name="action_url" :value="form.actionUrl" />
        <input type="hidden" name="image_url" :value="form.imageUrl" />
        <input type="hidden" name="audience_type" :value="form.audienceType" />
        <input type="hidden" name="work_shift_id" :value="form.workShiftId" />
        <input type="hidden" name="employee_numbers" :value="selectedEmployeeNumbers" />
        <input type="hidden" name="action" :value="formAction" />

        <oxd-form-row>
          <div class="orangehrm-notifications-switch">
            <div>
              <oxd-text tag="p" class="orangehrm-module-field-label">
                GroupHR In-App Notifications
              </oxd-text>
              <oxd-text tag="p" class="orangehrm-notifications-help">
                Messages appear inside the employee PWA inbox. No external push account is required.
              </oxd-text>
            </div>
            <oxd-switch-input v-model="configuration.enabled" />
          </div>
        </oxd-form-row>

        <oxd-divider class="orangehrm-form-divider" />

        <div class="orangehrm-notifications-stats">
          <div>
            <strong>{{ stats.active || 0 }}</strong>
            <span>Active Messages</span>
          </div>
          <div>
            <strong>{{ stats.today || 0 }}</strong>
            <span>Sent Today</span>
          </div>
          <div>
            <strong>{{ stats.unread || 0 }}</strong>
            <span>Unread Inbox Items</span>
          </div>
        </div>

        <oxd-divider class="orangehrm-form-divider" />

        <oxd-text tag="p" class="orangehrm-subtitle">
          Send Company Announcement
        </oxd-text>

        <div class="orangehrm-notifications-target">
          <label>
            <span>Send To</span>
            <select v-model="form.audienceType">
              <option value="all">All Employees</option>
              <option value="work_shift">Work Shift Employees</option>
              <option value="employees">Selected Employees</option>
            </select>
          </label>

          <label v-if="form.audienceType === 'work_shift'">
            <span>Work Shift</span>
            <select v-model="form.workShiftId">
              <option value="all">All Work Shifts</option>
              <option
                v-for="shift in workShifts"
                :key="shift.id"
                :value="String(shift.id)"
              >
                {{ shift.name }} - {{ shift.startTime }} to {{ shift.endTime }} ({{ shift.employeeCount }} employees)
              </option>
            </select>
          </label>

          <div
            v-if="form.audienceType === 'employees'"
            class="orangehrm-notifications-employees"
          >
            <label>
              <span>Search Employees</span>
              <input
                v-model="employeeSearch"
                type="search"
                placeholder="Type employee name, number, or work shift"
              />
            </label>
            <div class="orangehrm-notifications-employee-actions">
              <button type="button" @click="selectFilteredEmployees">
                All Select
              </button>
              <button type="button" @click="clearEmployees">
                Clear
              </button>
              <strong>{{ form.employeeNumbers.length }} selected</strong>
            </div>
            <select v-model="form.employeeNumbers" multiple size="8">
              <option
                v-for="employee in filteredEmployees"
                :key="employee.empNumber"
                :value="String(employee.empNumber)"
              >
                {{ employee.name }} / {{ employee.workShift }}
              </option>
            </select>
          </div>

          <p>{{ selectedAudienceSummary }}</p>
        </div>

        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field v-model="form.title" label="Title" />
            </oxd-grid-item>
            <oxd-grid-item>
              <label class="orangehrm-notifications-select">
                <span>Category</span>
                <select v-model="form.category">
                  <option
                    v-for="category in categories"
                    :key="category.value"
                    :value="category.value"
                  >
                    {{ category.label }}
                  </option>
                </select>
              </label>
            </oxd-grid-item>
            <oxd-grid-item class="--span-column-2">
              <oxd-input-field
                v-model="form.body"
                type="textarea"
                label="Message"
              />
            </oxd-grid-item>
            <oxd-grid-item class="--span-column-2">
              <oxd-input-field
                v-model="form.actionUrl"
                label="Action Link"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="form.imageUrl"
                label="Image URL"
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <label class="orangehrm-notifications-file">
                <span>Attachment Upload</span>
                <input
                  name="attachment_file"
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                />
                <em>PDF, image, Word, Excel, CSV, or text. Max 10 MB.</em>
              </label>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            label="Open PWA"
            type="button"
            @click="openPwa"
          />
          <oxd-button
            display-type="secondary"
            label="Send Notification"
            type="submit"
            @click="formAction = 'send'"
          />
          <oxd-button
            display-type="secondary"
            label="Save"
            type="submit"
            @click="formAction = 'save'"
          />
        </oxd-form-actions>
      </form>
    </div>
  </div>
</template>

<script>
import {OxdSwitchInput} from '@ohrm/oxd';

export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
  },
  props: {
    settings: {
      type: Object,
      required: true,
    },
    message: {
      type: String,
      default: '',
    },
    messageType: {
      type: String,
      default: '',
    },
    pwaUrl: {
      type: String,
      required: true,
    },
    workShifts: {
      type: Array,
      default: () => [],
    },
    employees: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    stats: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      configuration: {...this.settings},
      form: {
        title: 'GroupHR update',
        body: '',
        category: Array.isArray(this.categories) &&
          this.categories.some((category) => category.value === 'announcement')
          ? 'announcement'
          : 'general',
        actionUrl: '',
        imageUrl: '',
        audienceType: 'all',
        workShiftId: 'all',
        employeeNumbers: [],
      },
      employeeSearch: '',
      currentUrl: window.location.href,
      formAction: 'save',
    };
  },
  computed: {
    filteredEmployees() {
      const search = this.employeeSearch.trim().toLowerCase();
      const employees = Array.isArray(this.employees) ? this.employees : [];
      if (!search) {
        return employees.slice(0, 240);
      }
      return employees
        .filter((employee) => {
          const haystack = [
            employee.name,
            employee.employeeId,
            employee.empNumber,
            employee.workShift,
          ]
            .join(' ')
            .toLowerCase();
          return haystack.includes(search);
        })
        .slice(0, 240);
    },
    selectedEmployeeNumbers() {
      return this.form.employeeNumbers.join(',');
    },
    selectedAudienceSummary() {
      if (this.form.audienceType === 'all') {
        return `Will send to all active employees (${this.employees.length}).`;
      }
      if (this.form.audienceType === 'work_shift') {
        if (this.form.workShiftId === 'all') {
          const total = this.workShifts.reduce(
            (sum, shift) => sum + Number(shift.employeeCount || 0),
            0,
          );
          return `Will send to employees assigned to any work shift (${total}).`;
        }
        const shift = this.workShifts.find(
          (item) => String(item.id) === String(this.form.workShiftId),
        );
        return shift
          ? `Will send to ${shift.employeeCount} employees in ${shift.name}.`
          : 'Choose a work shift.';
      }
      return `Will send to ${this.form.employeeNumbers.length} selected employees.`;
    },
  },
  methods: {
    openPwa() {
      window.open(this.pwaUrl, '_blank', 'noopener');
    },
    selectFilteredEmployees() {
      const selected = new Set(this.form.employeeNumbers);
      const source = this.employeeSearch.trim()
        ? this.filteredEmployees
        : this.employees;
      source.forEach((employee) => {
        selected.add(String(employee.empNumber));
      });
      this.form.employeeNumbers = Array.from(selected);
    },
    clearEmployees() {
      this.form.employeeNumbers = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-notifications-switch {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #e8eaef;
  border-radius: 0.65rem;
  padding: 0.85rem 1rem;
}

.orangehrm-notifications-help {
  color: #64728c;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.orangehrm-notifications-stats {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  div {
    border: 1px solid #e8eaef;
    border-radius: 0.65rem;
    padding: 0.85rem 1rem;
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #f26322;
    font-size: 1.15rem;
  }

  span {
    color: #64728c;
    font-size: 0.75rem;
    font-weight: 700;
    margin-top: 0.2rem;
  }
}

.orangehrm-notifications-message {
  border-radius: 0.65rem;
  font-weight: 700;
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;

  &.--success {
    background: #edf7e7;
    color: #386b12;
  }

  &.--error {
    background: #fff1f1;
    color: #b42318;
  }
}

.orangehrm-notifications-file {
  display: grid;
  gap: 0.4rem;
  color: #64728c;
  font-size: 0.75rem;
  font-weight: 700;

  span {
    color: #64728c;
  }

  input {
    width: 100%;
    border: 1px solid #e8eaef;
    border-radius: 0.65rem;
    background: #fff;
    color: #38455d;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.7rem;
  }

  em {
    color: #8a96ab;
    font-size: 0.68rem;
    font-style: normal;
  }
}

.orangehrm-notifications-select {
  display: grid;
  gap: 0.4rem;
  color: #64728c;
  font-size: 0.75rem;
  font-weight: 700;

  select {
    width: 100%;
    min-height: 2.75rem;
    border: 1px solid #e8eaef;
    border-radius: 0.65rem;
    background: #fff;
    color: #38455d;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0 0.75rem;
  }
}

.orangehrm-notifications-target {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 1rem 0 1.25rem;
  padding: 1rem;
  border: 1px solid #e8eaef;
  border-radius: 0.65rem;
  background: #fbfcfe;

  label {
    display: grid;
    gap: 0.4rem;
    color: #64728c;
    font-size: 0.75rem;
    font-weight: 700;
  }

  select,
  input {
    width: 100%;
    min-height: 2.75rem;
    border: 1px solid #e8eaef;
    border-radius: 0.65rem;
    background: #fff;
    color: #38455d;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0 0.75rem;
  }

  p {
    grid-column: 1 / -1;
    color: #64728c;
    font-size: 0.75rem;
    font-weight: 700;
    margin: 0;
  }
}

.orangehrm-notifications-employees {
  display: grid;
  gap: 0.6rem;
  grid-column: 1 / -1;

  select {
    min-height: 13rem;
    padding: 0.45rem;
  }

  option {
    padding: 0.35rem 0.45rem;
  }
}

.orangehrm-notifications-employee-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;

  button {
    min-height: 2rem;
    border: 0;
    border-radius: 999px;
    background: #fff1e8;
    color: #c44f10;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 800;
    padding: 0 0.85rem;
  }

  strong {
    color: #64728c;
    font-size: 0.75rem;
    margin-left: auto;
  }
}

@media (max-width: 760px) {
  .orangehrm-notifications-switch,
  .orangehrm-notifications-stats,
  .orangehrm-notifications-target {
    grid-template-columns: 1fr;
  }

  .orangehrm-notifications-switch {
    align-items: flex-start;
    flex-direction: column;
  }

}
</style>
